import React, { useEffect, useRef, useState, useCallback } from "react";
import { motion } from "framer-motion";
import SquishyButton from "../components/SquishyButton";
import { playFreq } from "../lib/sounds";
import { speak } from "../lib/speech";

// A colourful 7-key instrument (C major). Two modes:
//  - "free": just play music freely, finish when ready.
//  - "play": listen to a growing melody and repeat it (Simon-style).
const NOTES = [
  { name: "do", freq: 261.63, color: "#F87171", edge: "#DC2626" },
  { name: "re", freq: 293.66, color: "#FB923C", edge: "#EA580C" },
  { name: "mi", freq: 329.63, color: "#FDE047", edge: "#EAB308" },
  { name: "fa", freq: 349.23, color: "#4ADE80", edge: "#16A34A" },
  { name: "sol", freq: 392.0, color: "#38BDF8", edge: "#0284C7" },
  { name: "la", freq: 440.0, color: "#818CF8", edge: "#4F46E5" },
  { name: "si", freq: 493.88, color: "#F472B6", edge: "#DB2777" },
];

const TOTAL_ROUNDS = 4; // lengths 2,3,4,5

export default function MusicKeys({ mode = "free", onFinish }) {
  const [active, setActive] = useState(null);
  const [sequence, setSequence] = useState([]);
  const [step, setStep] = useState(0);
  const [locked, setLocked] = useState(false);
  const [round, setRound] = useState(0);
  const correctRef = useRef(0);

  const flash = (idx, dur = 400) => {
    setActive(idx);
    setTimeout(() => setActive((a) => (a === idx ? null : a)), dur);
  };

  const hit = (idx) => {
    playFreq(NOTES[idx].freq, 0.5);
    flash(idx, 250);
  };

  const playSequence = useCallback(async (seq) => {
    setLocked(true);
    await new Promise((r) => setTimeout(r, 600));
    for (const idx of seq) {
      playFreq(NOTES[idx].freq, 0.45);
      setActive(idx);
      await new Promise((r) => setTimeout(r, 550));
      setActive(null);
      await new Promise((r) => setTimeout(r, 180));
    }
    setLocked(false);
    setStep(0);
  }, []);

  const startRound = useCallback((r) => {
    const len = r + 2; // 2,3,4,5
    const seq = Array.from({ length: len }, () => Math.floor(Math.random() * NOTES.length));
    setSequence(seq);
    speak("Luister goed en speel na!");
    playSequence(seq);
  }, [playSequence]);

  useEffect(() => {
    if (mode === "play") startRound(0);
    else speak("Maak je eigen muziek! Tik op de gekleurde toetsen.");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const pressPlay = (idx) => {
    if (locked) return;
    hit(idx);
    if (idx === sequence[step]) {
      const nextStep = step + 1;
      if (nextStep >= sequence.length) {
        // round complete
        correctRef.current += 1;
        setLocked(true);
        speak("Goed gedaan!");
        const nextRound = round + 1;
        setTimeout(() => {
          if (nextRound >= TOTAL_ROUNDS) onFinish(correctRef.current, TOTAL_ROUNDS);
          else { setRound(nextRound); startRound(nextRound); }
        }, 900);
      } else {
        setStep(nextStep);
      }
    } else {
      // wrong -> replay the same sequence
      speak("Probeer het nog eens!");
      setTimeout(() => playSequence(sequence), 500);
    }
  };

  return (
    <div className="flex w-full flex-col items-center gap-6">
      {mode === "play" && (
        <div className="flex items-center gap-3">
          <p data-testid="music-round" className="font-display text-lg font-bold text-slate-600">
            Melodie {round + 1} / {TOTAL_ROUNDS}
          </p>
          <SquishyButton data-testid="music-replay-btn" onClick={() => !locked && playSequence(sequence)}
            color="#38BDF8" edge="#0284C7" className="text-sm px-4 py-2">🔊 Opnieuw</SquishyButton>
        </div>
      )}

      <div className="flex items-end justify-center gap-2 sm:gap-3">
        {NOTES.map((n, i) => (
          <motion.button
            key={n.name} data-testid={`music-key-${i}`}
            onClick={() => (mode === "play" ? pressPlay(i) : hit(i))}
            whileTap={{ y: 6 }}
            animate={{ y: active === i ? 6 : 0, filter: active === i ? "brightness(1.25)" : "brightness(1)" }}
            style={{ backgroundColor: n.color, boxShadow: `0 6px 0 ${n.edge}` }}
            className="flex h-32 w-11 items-end justify-center rounded-2xl pb-2 font-display text-xs font-bold text-white sm:h-40 sm:w-14 no-select"
          >
            {n.name}
          </motion.button>
        ))}
      </div>

      {mode === "free" && (
        <SquishyButton data-testid="music-done-btn" onClick={() => onFinish(1, 1)} color="#4ADE80" edge="#16A34A" className="text-xl px-10">
          Klaar! 🎉
        </SquishyButton>
      )}
    </div>
  );
}
